import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@strivelabs/ui-core/v2';
import { Plus, Tag, X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useLabels } from '@/hooks/useTasks';
import { LABEL_CHIP } from '../taskTheme';
import { INLINE_FIELD_BOX, INLINE_FIELD_POPOVER_TRIGGER } from './TaskInlinePicker';
import type { TaskLabel } from '@/types/task';

/**
 * Mirrors `LABEL_NAME_PATTERN` in `apps/server/src/label/constant.ts`, so a name
 * the server would refuse is caught while it is being typed.
 *
 * Keep the two in step: this copy is the one a person actually meets, so when
 * it is the stricter of the pair the server's rule never gets a say and a name
 * it would have accepted looks invalid.
 */
const LABEL_PATTERN = /^[a-z0-9_-](?:[a-z0-9 _-]*[a-z0-9_-])?$/;

/** Lowered and whitespace-collapsed, exactly as the server stores it. */
const normalise = (value: string) => value.trim().toLowerCase()
  .replace(/\s+/g, ' ');

/** The gap between chips, in step with the trigger's `gap-1.5`. */
const CHIP_GAP_PX = 6;

/**
 * How many of the chips, laid out left to right, fit in `available` while
 * leaving room for the `+N` chip whenever some are left over. Always at least
 * one — a lone chip too wide for the row truncates instead. An unmeasured row
 * (0 wide) shows one, so the first paint never overflows.
 */
export const chipsThatFit = (available: number, widths: number[], moreWidth: number) => {
  if (available <= 0) {
    return Math.min(1, widths.length);
  }

  let used = 0;

  for (let index = 0; index < widths.length; index += 1) {
    used += (index ? CHIP_GAP_PX : 0) + widths[index];

    const left = widths.length - index - 1;
    const reserve = left ? CHIP_GAP_PX + moreWidth : 0;

    if (used + reserve > available) {
      return Math.max(index, 1);
    }
  }

  return widths.length;
};

/** A label the task holds, as a removable tag in the editor's strip. */
const HeldTag = ({ name, onRemove }: { name: string; onRemove: () => void }) => (
  <span className={cn(LABEL_CHIP, 'inline-flex items-center gap-1 bg-white pr-[5px]')}>
    <span className="truncate">{name}</span>
    <button
      type="button"
      aria-label={`Remove ${name}`}
      className="shrink-0 rounded-[3px] text-[#9A9A9A] hover:text-[#181818]"
      onClick={onRemove}
    >
      <X className="size-3" aria-hidden />
    </button>
  </span>
);

interface TaskLabelsEditorProps {
  labels: TaskLabel[];
  disabled?: boolean;
  onChange: (names: string[]) => void;
}

/**
 * The task's labels.
 *
 * The workspace's existing labels are offered before a new one is made, which
 * is what keeps "paid search" and "paid-search" from both existing. Names are
 * replaced wholesale rather than added to, because that is what the API takes.
 */
export const TaskLabelsEditor = ({
  labels, disabled = false, onChange,
}: TaskLabelsEditorProps) => {
  const [ open, setOpen ] = useState(false);
  const [ query, setQuery ] = useState('');
  const { data: known = [] } = useLabels();
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [ shown, setShown ] = useState(1);

  // As many chips as the row has room for, re-counted as the panel is
  // resized. Measured off an invisible copy of every chip, so the count never
  // depends on what the last count happened to draw.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;

    if (!row || !measure) {
      return undefined;
    }

    const fit = () => {
      const chips = [ ...measure.children ] as HTMLElement[];
      const more = chips.pop();

      setShown(chipsThatFit(
        // The wrapper bleeds 9px each side and the button pads 9px back in,
        // so the chips have exactly the row's width.
        row.clientWidth,
        chips.map((chip) => chip.offsetWidth),
        more?.offsetWidth ?? 0,
      ));
    };

    fit();

    const observer = new ResizeObserver(fit);

    observer.observe(row);

    return () => observer.disconnect();
  }, [ labels ]);

  const hidden = labels.slice(shown);

  const typed = normalise(query);
  const held = useMemo(() => labels.map((label) => label.name), [ labels ]);

  const offered = useMemo(
    () => known.filter((label) => label.name.includes(typed) && !held.includes(label.name)),
    [ known, typed, held ],
  );

  const creatable = typed
    && LABEL_PATTERN.test(typed)
    && !known.some((label) => label.name === typed);

  const toggle = (name: string) => onChange(held.includes(name)
    ? held.filter((each) => each !== name)
    : [ ...held, name ]);

  return (
    <div ref={rowRef} className="relative min-w-0 flex-1">
      {/* Every chip at its natural width, for the count above. Never seen.
          In a zero-height clipping box: laid out `w-max` on its own, a long
          run of chips widened the panel's scrolling body and scrolled it
          sideways. Clipping leaves `offsetWidth` unchanged, and the box sits
          inside the row rather than the row clipping, which would cut off the
          trigger's hover bleed. */}
      <div aria-hidden className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden">
        <div ref={measureRef} className="flex w-max gap-1.5">
          {labels.map((label) => (
            <span key={label.id} className={cn(LABEL_CHIP, 'shrink-0')}>{label.name}</span>
          ))}
          <span className={cn(LABEL_CHIP, 'shrink-0')}>+{labels.length}</span>
        </div>
      </div>

      <Popover
        open={open}
        onOpenChange={(next) => { setOpen(next); setQuery(''); }}
        side="bottom"
        align="start"
        triggerClassName={INLINE_FIELD_POPOVER_TRIGGER}
        trigger={
          <button
            type="button"
            aria-label="Labels"
            disabled={disabled}
            className={cn(INLINE_FIELD_BOX, 'gap-1.5', disabled && 'pointer-events-none')}
          >
            {labels.length === 0 ? (
              <>
                {/* Same grey, glyph and gap as the other empty properties. */}
                <Tag className="size-4 shrink-0 text-[#9A9A9A]" aria-hidden />
                <span className="ml-[1px] text-[13.5px] text-[#9A9A9A]">Add label</span>
              </>
            ) : (
              <>
                {labels.slice(0, shown).map((label, index) => (
                  <span
                    key={label.id}
                    // A lone chip too wide for the row truncates; any more than
                    // one were counted to fit whole.
                    className={cn(LABEL_CHIP, shown === 1 && index === 0 ? 'min-w-0 truncate' : 'shrink-0')}
                  >
                    {label.name}
                  </span>
                ))}
                {hidden.length ? (
                  <span
                    className={cn(LABEL_CHIP, 'shrink-0')}
                    title={hidden.map((label) => label.name).join(', ')}
                  >
                    +{hidden.length}
                  </span>
                ) : null}
              </>
            )}
          </button>
        }
      >
        <div className="w-[248px]">
          <div className="-m-1 mb-1.5 flex flex-wrap items-center gap-1.5 rounded-t-[10px] border-b border-[rgba(0,0,0,0.06)] bg-[#FBFAF7] px-2.5 py-2">
            {labels.map((label) => (
              <HeldTag key={label.id} name={label.name} onRemove={() => toggle(label.name)} />
            ))}
            <input
              value={query}
              autoFocus
              aria-label="Find or create a label"
              placeholder="Find or create"
              className="min-w-[110px] flex-1 border-0 bg-transparent py-0.5 text-[13px] text-[#181818] outline-none placeholder:text-[#9A9A9A]"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && creatable) {
                  event.preventDefault();
                  toggle(typed);
                  setQuery('');
                }
              }}
            />
          </div>

          <p className="px-[9px] pb-1.5 pt-1 text-[11.5px] text-[#9A9A9A]">
            Select an option or create one
          </p>

          <div className="max-h-[196px] overflow-y-auto">
            {offered.map((label) => (
              <button
                key={label.id}
                type="button"
                className="flex w-full items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left hover:bg-[#FBFBFB]"
                onClick={() => toggle(label.name)}
              >
                <span className={LABEL_CHIP}>{label.name}</span>
              </button>
            ))}

            {creatable ? (
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left text-[13.5px] text-[#9A9A9A] hover:text-[#696969]"
                onClick={() => { toggle(typed); setQuery(''); }}
              >
                <Plus className="size-[15px] shrink-0" aria-hidden />
                Create
                <span className={LABEL_CHIP}>{typed}</span>
              </button>
            ) : null}

            {offered.length === 0 && !creatable ? (
              <p className="flex items-center gap-2 px-[9px] py-2 text-[12.5px] text-[#9A9A9A]">
                <Tag className="size-3.5 shrink-0" aria-hidden />
                {typed ? 'That is not a label name' : 'No labels yet'}
              </p>
            ) : null}
          </div>
        </div>
      </Popover>
    </div>
  );
};
